import { useState, Fragment, useEffect } from "react";
import {
  Box,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Checkbox,
  Typography,
  Button,
  Divider,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import toast, { Toaster } from "react-hot-toast";
import AppBreadcrumbs from "./../../components/AppBreadcrumbs";
import * as services from "./../../services/saleng";

export default function PermissionTable() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const ui = {
    headerGrad: isDark
      ? "linear-gradient(90deg, #2E7D32 0%, #1B5E20 50%, #2E7D32 100%)"
      : "linear-gradient(90deg, #E8F5E9 0%, #DFF4EA 60%, #E8F5E9 100%)",
    groupGrad: isDark
      ? "linear-gradient(90deg, #1B5E20 0%, #2E7D32 60%, #1B5E20 100%)"
      : "linear-gradient(90deg, #E8F5E9 0%, #DFF4EA 60%, #E8F5E9 100%)",
    zebraOverlay: isDark
      ? "linear-gradient(0deg, rgba(255,255,255,0.04), rgba(255,255,255,0.04))"
      : "linear-gradient(0deg, rgba(0,0,0,0.03), rgba(0,0,0,0.03))",
    footerGrad: isDark
      ? "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(38,50,56,0.9) 60%, #263238 100%)"
      : "linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(240,244,248,0.9) 60%, #F0F4F8 100%)",
    border: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)",
    primary: "#43A047",
    paperBg: isDark ? "#1E1E1E" : "#fff",
    textColor: isDark ? "#E0E0E0" : "#212121",
  };

  const roles = [
    "CEO",
    "Exclusive Admin",
    "Manager",
    "Branch Admin",
    "Owner",
    "Branch Manager",
    "Cashier",
    "Scale",
  ];

  const rolesTh = {
    CEO: "CEO",
    "Exclusive Admin": "Exclusive Admin",
    Manager: "Manager สำนักงานใหญ่",
    "Branch Admin": "Admin สาขา",
    Owner: "Owner แฟรนไชส์",
    "Branch Manager": "Manager สาขา",
    Cashier: "Cashier",
    Scale: "Scale",
  };

  const permissionGroups = [
    {
      group: "หมวดจัดการสาขา",
      items: [
        { key: "switch_branches", label: "แสดงปุ่มเปลี่ยนสาขา" },
      ],
    },
    {
      group: "หมวดเปิดตั๋ว (ซื้อ-ขาย)",
      items: [
        { key: "ticket_open_purchase_sale", label: "เปิดตั๋ว (ซื้อ-ขาย)" },
        {
          key: "edit_total_weight",
          label: "แก้ไขน้ำหนักรวมจากเครื่องคิดเลขหรือคีย์บอร์ด (ซื้อ-ขาย)",
        },
        {
          key: "edit_deduct_weight",
          label: "แก้ไขน้ำหนักหักจากเครื่องคิดเลขหรือคีย์บอร์ด (ซื้อ-ขาย)",
        },
        {
          key: "edit_impurity",
          label: "แก้ไขสิ่งเจือปนจากเครื่องคิดเลขหรือคีย์บอร์ด (ซื้อ-ขาย)",
        },
        {
          key: "edit_price_free",
          label:
            "แก้ไขราคาสินค้าในตั๋วจากเครื่องคิดเลขหรือคีย์บอร์ด (ซื้อ-ขาย)",
        },
        {
          key: "show_price_in_ticket",
          label: "แสดงราคาสินค้าในตั๋ว (ซื้อ-ขาย)",
        },
        { key: "view_user_in_ticket", label: "เห็นผู้ใช้งานเมื่อเปิดดูตั๋ว" },
        { key: "view_paid_ticket", label: "ดูรายตั๋วจ่ายแล้ว" },
        {
          key: "view_ticket_history",
          label: "ดูประวัติการทำรายการในตั๋ว",
        },
        { key: "Print", label: "Print A4" },
        { key: "purchase_pay_all", label: "สั่งจ่ายเงิน (ทุกช่องทาง)" },
        {
          key: "purchase_cancel_payment",
          label: "ยกเลิกการจ่ายเงิน (ตั๋วจ่ายแล้ว)",
        },
        { key: "purchase_cancel_ticket", label: "ยกเลิกตั๋ว" },
      ],
    },
    {
      group: "จัดการสินค้า",
      items: [
        {
          key: "product_manage_items",
          label: "จัดการสินค้า (เพิ่ม-ลบรายการสินค้า)",
        },
        { key: "product_set_price", label: "จัดการสินค้า (ตั้งราคาสินค้า)" },
      ],
    },
    {
      group: "รายงาน",
      items: [
        { key: "report_purchase_items", label: "รายงานการซื้อสินค้า" },
        { key: "report_sale_items", label: "รายงานการขายสินค้า" },
        {
          key: "report_show_cost",
          label: "แสดงต้นทุนในรายงานขาย (ต้นทุนต่อหน่วย และต้นทุนรวม)",
        },
        {
          key: "report_show_profit",
          label: "แสดงกำไรในรายงานขาย (กำไรต่อหน่วยและกำไรรวม)",
        },
      ],
    },
    {
      group: "จัดการลูกค้า",
      items: [
        { key: "customer_manage", label: "จัดการลูกค้า" },
        {
          key: "customer_group_manage_ratio",
          label: "จัดการกลุ่มลูกค้า (แก้สัดส่วนราคาแต่ละกลุ่ม)",
        },
      ],
    },
    {
      group: "สต๊อกสินค้า",
      items: [
        { key: "inventory_view_stock", label: "เห็นจำนวนสต็อคสินค้า" },
        {
          key: "inventory_view_changes",
          label: "เห็นการเปลี่ยนแปลงของต้นทุนและคลัง",
        },
      ],
    },
    {
      group: "ใบเสนอราคา / PO",
      items: [
        { key: "quotation_manage", label: "ออก PO" },
        {
          key: "po_edit_price",
          label: "แก้ไขราคาสินค้าใน PO",
        },
        {
          key: "po_edit_locked_weight",
          label: "แก้ไขน้ำหนักที่ล็อค PO หลังจากที่กด Approve แล้ว",
        },
      ],
    },
    {
      group: "จัดการตู้ ATM",
      items: [{ key: "atm_manage", label: "จัดการตู้ ATM" }],
    },
  ];

  // รวม key ทั้งหมดไว้ใช้ตอน build payload
  const buildAllKeys = () =>
    permissionGroups.flatMap((g) => g.items.map((it) => it.key));

  const [permissions, setPermissions] = useState(() => {
    const init = {};
    roles.forEach((r) => (init[r] = {}));
    return init;
  });

  // เก็บ roles ที่มาจาก API เพื่อหา id และ title ตอนบันทึก
  const [rolesFromApi, setRolesFromApi] = useState([]);
  const [saving, setSaving] = useState(false);

  const toggle = (role, key) => {
    setPermissions((prev) => ({
      ...prev,
      [role]: { ...prev[role], [key]: !prev[role]?.[key] },
    }));
  };

  const makeFullPermsForRole = (roleKey) => {
    const allKeys = buildAllKeys();
    const src = permissions[roleKey] || {};
    const full = {};
    for (const k of allKeys) full[k] = !!src[k];
    return full;
  };

  // กดบันทึก: map title จาก roles ที่ get มา → หา key (lowercase) → ส่ง permissions ครบทุก key เสมอ
  const handleSave = async () => {
    try {
      setSaving(true);

      if (!Array.isArray(rolesFromApi) || rolesFromApi.length === 0) {
        toast.error("ยังไม่มีข้อมูลสิทธิ์การเข้าถึงจากเซิร์ฟเวอร์");
        return;
      }

      const jobs = rolesFromApi
        .map((role) => {
          const key = String(role.title || "")
          if (!roles.includes(key)) return null;

          const payload = { permissions: makeFullPermsForRole(key) };

          return services.patchRole(role.id, payload);
        })
        .filter(Boolean);

      if (!jobs.length) {
        toast("ไม่มีอะไรให้บันทึก");
        return;
      }

      const results = await Promise.allSettled(jobs);
      const fails = results.filter((r) => r.status === "rejected");

      if (fails.length) {
        toast.error(`บันทึกบางสิทธิ์ไม่สำเร็จ (${fails.length})`);
      } else {
        toast.success("บันทึกสิทธิ์สำเร็จ");
      }
    } catch (e) {
      toast.error(e?.message ?? "เกิดข้อผิดพลาด");
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await services.getRoles();
        const list = res.data.data || [];

        setRolesFromApi(list);

        const next = {};
        roles.forEach((r) => (next[r] = { ...permissions[r] }));

        list.forEach((role) => {
          const key = String(role.title || "");
          if (!roles.includes(key)) return;

          const perms = role.permissions;
          if (Array.isArray(perms)) {
            perms.forEach((p) => {
              if (p && typeof p.key === "string") {
                next[key][p.key] = !!p.allowed;
              }
            });
          } else if (perms && typeof perms === "object") {
            Object.entries(perms).forEach(([k, v]) => {
              next[key][k] = !!v;
            });
          }
        });

        setPermissions(next);
      } catch (e) {
        console.error(e);
        toast.error("โหลดข้อมูลสิทธิ์การเข้าถึงไม่สำเร็จ");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Toaster />
      <AppBreadcrumbs />
      <Paper
        elevation={0}
        sx={{
          overflow: "hidden",
          borderRadius: 3,
          border: `1px solid ${ui.border}`,
          bgcolor: ui.paperBg,
          color: ui.textColor,
          mt: 2,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 2.5,
            pb: 1.5,
            color: "white",
            borderBottom: `1px solid ${ui.border}`,
            borderTopLeftRadius: 12,
            borderTopRightRadius: 12,
            background:
              "linear-gradient(90deg, #2E7D32 0%, #43A047 50%, #66BB6A 100%)",
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            สิทธิ์ในการเข้าถึง
          </Typography>
          <Typography
            variant="body2"
            sx={{ opacity: 0.9, color: "rgba(255,255,255,0.85)" }}
          >
            ตั้งค่าสิทธิ์แยกตามหมวด และกำหนดได้รายบทบาทผู้ใช้งาน
          </Typography>
        </Box>

        <Divider />

        <TableContainer
          sx={{
            maxHeight: "calc(100vh - 340px)",
            "& .MuiTableCell-root": { fontSize: 14, color: ui.textColor },
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    fontWeight: 700,
                    background: ui.headerGrad,
                    borderRight: `1px solid ${ui.border}`,
                    minWidth: 420,
                    color: isDark ? "#E0E0E0" : "#212121",
                    height: 50,
                  }}
                >
                  รายการสิทธิ์
                </TableCell>
                {roles.map((r) => (
                  <TableCell
                    key={r}
                    align="center"
                    sx={{
                      fontWeight: 700,
                      background: ui.headerGrad,
                      minWidth: 140,
                      color: isDark ? "#E0E0E0" : "#212121",
                    }}
                  >
                    {rolesTh[r] ?? r}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {permissionGroups.map((group) => (
                <Fragment key={group.group}>
                  <TableRow>
                    <TableCell
                      colSpan={1 + roles.length}
                      sx={{
                        background: ui.groupGrad,
                        color: isDark ? "#E0E0E0" : "text.primary",
                        fontWeight: 700,
                        borderTop: `1px solid ${ui.border}`,
                        height: 50,
                      }}
                    >
                      {group.group}
                    </TableCell>
                  </TableRow>

                  {group.items.map((item) => (
                    <TableRow
                      key={item.key}
                      hover
                      sx={{
                        "&:nth-of-type(even)": {
                          backgroundImage: ui.zebraOverlay,
                        },
                      }}
                    >
                      <TableCell sx={{ borderRight: `1px solid ${ui.border}` }}>
                        {item.label}
                      </TableCell>

                      {roles.map((r) => (
                        <TableCell align="center" key={`${r}-${item.key}`}>
                          <Checkbox
                            checked={!!permissions[r]?.[item.key]}
                            onChange={() => toggle(r, item.key)}
                            sx={{ "&.Mui-checked": { color: ui.primary } }}
                          />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Divider />
        <Box
          sx={{
            position: "sticky",
            bottom: 0,
            background: ui.footerGrad,
            p: 2,
            display: "flex",
            justifyContent: "flex-end",
            gap: 1.5,
            borderTop: `1px solid ${ui.border}`,
            backdropFilter: "saturate(120%) blur(6px)",
          }}
        >
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            sx={{
              background:
                "linear-gradient(90deg, #43A047 0%, #4CAF50 50%, #66BB6A 100%)",
              boxShadow:
                "0 6px 16px rgba(76,175,80,0.28), 0 2px 6px rgba(0,0,0,0.06)",
              "&:hover": {
                background:
                  "linear-gradient(90deg, #388E3C 0%, #43A047 50%, #4CAF50 100%)",
              },
            }}
          >
            {saving ? "กำลังบันทึก..." : "บันทึกสิทธิ์"}
          </Button>
        </Box>
      </Paper>
    </>
  );
}
